/**
 * Talking to Dr Cleanstein's engine (the ask-chat edge function) and reading
 * stored chats back. The engine streams one JSON event per line; stored chats
 * are read straight from the database, where row rules show a person only
 * their own.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { AssistantRole } from '../../config/assistant';
import { ASSISTANT_AVATARS, STAFF_ASSISTANT_NAME } from '../../config/assistant';

export type AskEvent =
  | { type: 'conversation'; id: string }
  | { type: 'step'; label: string }
  | { type: 'text'; text: string }
  | { type: 'done'; status: string; cost_usd: number }
  | { type: 'error'; message: string };

export interface ConversationSummary {
  id: string;
  title: string | null;
  updated_at: string;
  /** Starred and filed in a project, as in Claude; shared with CMB Chats (Gary, 18 Oct). */
  starred_at?: string | null;
  project_id?: string | null;
}

export interface AskProject {
  id: string;
  name: string;
}

export interface DisplayMessage {
  /** Stored id of the last message in this bubble; used to log a query against it. */
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

/** The Ask button and side panel are Ted's; a chat with another assistant uses ASSISTANT_AVATARS. */
export const AVATAR_SRC = ASSISTANT_AVATARS.ted;

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-chat`;
const PAGE_NOTE = /\n\n\(Asked from the portal page [^)]*\)\.?$/;

async function authHeaders(supabase: SupabaseClient, json = true): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('You are signed out. Please sign in again.');
  const auth = { Authorization: `Bearer ${session.access_token}` };
  return json ? { ...auth, 'Content-Type': 'application/json' } : auth;
}

/** Ask a question and receive the answer as it is written. Resolves when the turn ends. */
export async function askQuestion(
  supabase: SupabaseClient,
  params: { text: string; conversationId: string | null; pagePath: string | null; role?: AssistantRole },
  onEvent: (event: AskEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    signal,
    headers: await authHeaders(supabase),
    body: JSON.stringify({
      action: 'ask',
      role: params.role ?? 'ted',
      text: params.text,
      conversation_id: params.conversationId,
      page_path: params.pagePath,
    }),
  });

  if (!response.ok || !response.body) {
    let message = `${STAFF_ASSISTANT_NAME} is not available right now.`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep the plain message
    }
    onEvent({ type: 'error', message });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const emit = (line: string) => {
    const event = parseEvent(line);
    if (event) onEvent(event);
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      emit(buffer.slice(0, newline));
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf('\n');
    }
  }
  emit(buffer + decoder.decode());
}

/** One stream line to a known event; anything malformed is skipped, never thrown. */
export function parseEvent(line: string): AskEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  switch (e.type) {
    case 'conversation': return typeof e.id === 'string' ? { type: 'conversation', id: e.id } : null;
    case 'step': return typeof e.label === 'string' ? { type: 'step', label: e.label } : null;
    case 'text': return typeof e.text === 'string' ? { type: 'text', text: e.text } : null;
    case 'error': return typeof e.message === 'string' ? { type: 'error', message: e.message } : null;
    case 'done':
      return {
        type: 'done',
        status: typeof e.status === 'string' ? e.status : 'done',
        cost_usd: typeof e.cost_usd === 'number' && Number.isFinite(e.cost_usd) ? e.cost_usd : 0,
      };
    default: return null;
  }
}

/** Log a query, with any screenshots or voice notes sent as a form alongside it. */
export async function logQuery(
  supabase: SupabaseClient,
  params: { conversationId: string; messageId: string | null; kind: 'wrong' | 'question'; comment: string; files: File[] },
): Promise<void> {
  const payload = JSON.stringify({
    action: 'log_query',
    conversation_id: params.conversationId,
    message_id: params.messageId,
    kind: params.kind,
    comment: params.comment,
  });
  let body: string | FormData = payload;
  if (params.files.length > 0) {
    const form = new FormData();
    form.append('payload', payload);
    for (const file of params.files) form.append('files', file, file.name);
    body = form;
  }
  // A form sets its own Content-Type (with the boundary), so only JSON names one here.
  let response: Response;
  try {
    response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: await authHeaders(supabase, params.files.length === 0),
      body,
    });
  } catch {
    throw new Error('Could not send. Check your connection and try again.');
  }
  let result: { ok?: boolean; error?: string } = {};
  try {
    result = (await response.json()) as { ok?: boolean; error?: string };
  } catch {
    // a gateway error page, not JSON
  }
  if (!response.ok || !result.ok) throw new Error(result.error ?? 'Could not log the query.');
}

export async function listConversations(supabase: SupabaseClient, userId: string, limit = 30, role: AssistantRole = 'ted'): Promise<ConversationSummary[]> {
  const { data, error } = await supabase
    .from('ask_conversations')
    .select('id, title, updated_at, starred_at, project_id')
    .eq('user_id', userId)
    // Each assistant keeps its own chats (Gary, 1 Nov: one engine, roles as settings).
    .eq('role', role)
    // Only chats started with Dr C. Help asked inside a CMB Chats customer chat, and
    // Suggest a reply drafts, stay with that chat (Gary, 15 Oct).
    .eq('origin', 'ask')
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as ConversationSummary[];
}

interface StoredBlock {
  type?: string;
  text?: string;
}

/**
 * Stored messages hold exactly what went to and from the model, lookups
 * included. Only the person's questions and the written answers are shown;
 * one answer can span several stored messages around its lookups.
 */
export async function loadConversation(supabase: SupabaseClient, conversationId: string): Promise<DisplayMessage[]> {
  const { data, error } = await supabase
    .from('ask_messages')
    .select('id, role, content')
    .eq('conversation_id', conversationId)
    .order('created_at');
  if (error) throw new Error(error.message);

  const out: DisplayMessage[] = [];
  for (const row of data ?? []) {
    const blocks = Array.isArray(row.content) ? (row.content as StoredBlock[]) : [];
    const text = blocks
      .filter((b) => b.type === 'text' && typeof b.text === 'string')
      .map((b) => b.text as string)
      .join('');
    if (!text) continue;
    const role = row.role === 'user' ? 'user' : 'assistant';
    const last = out[out.length - 1];
    if (role === 'assistant' && last?.role === 'assistant') {
      last.text += text;
      last.id = row.id as string;
    } else {
      out.push({ id: row.id as string, role, text: role === 'user' ? text.replace(PAGE_NOTE, '') : text });
    }
  }
  return out;
}

export async function deleteConversation(supabase: SupabaseClient, id: string): Promise<void> {
  const { error } = await supabase.from('ask_conversations').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function listProjects(supabase: SupabaseClient): Promise<AskProject[]> {
  const { data, error } = await supabase.from('ask_projects').select('id, name').order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as AskProject[];
}

export async function createProject(supabase: SupabaseClient, name: string): Promise<AskProject> {
  const { data, error } = await supabase.from('ask_projects').insert({ name: name.trim().slice(0, 80) }).select('id, name').single();
  if (error) throw new Error(error.message);
  return data as AskProject;
}

export async function renameProject(supabase: SupabaseClient, id: string, name: string): Promise<void> {
  const { error } = await supabase.from('ask_projects').update({ name: name.trim().slice(0, 80) }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Its chats go back to Recents (the database moves them). */
export async function deleteProject(supabase: SupabaseClient, id: string): Promise<void> {
  const { error } = await supabase.from('ask_projects').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function starConversation(supabase: SupabaseClient, id: string, starred: boolean): Promise<void> {
  const { error } = await supabase.from('ask_conversations').update({ starred_at: starred ? new Date().toISOString() : null }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function moveConversation(supabase: SupabaseClient, id: string, projectId: string | null): Promise<void> {
  const { error } = await supabase.from('ask_conversations').update({ project_id: projectId }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function renameConversation(supabase: SupabaseClient, id: string, title: string): Promise<void> {
  const { error } = await supabase.from('ask_conversations').update({ title: title.slice(0, 200) }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Today, Yesterday, Previous 7 days, Previous 30 days, Older: how the chat lists group. */
export function chatGroup(updatedAt: string, now = new Date()): string {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = new Date(updatedAt).getTime();
  const day = 86_400_000;
  if (t >= start) return 'Today';
  if (t >= start - day) return 'Yesterday';
  if (t >= start - 7 * day) return 'Previous 7 days';
  if (t >= start - 30 * day) return 'Previous 30 days';
  return 'Older';
}
